import { useRef } from "react";
import { useChatStore, ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { processMultiActionResponse } from "../lib/processMultiActionResponse";
import { reportErrorToSentry } from "@/zapi-frontend/utils/report-error-to-sentry";
import { confirmPollingEnd } from "@/zapi-frontend/shared/lib/confirmPollingEnd";
import { safeJsonParse } from "@/zapi-frontend/utils/safeJsonParse";
import { getStatusAll } from "../api/getStatus";
import { useQueryClient } from "@tanstack/react-query";
import { EGenerationStatus, EGenerationTaskType } from "../types";
import { processAssistantResponse } from "../lib/processAssistantResponse";
import { getProgressMessage } from "../lib/getProgressMessage";
import { useDiffStore } from "@/zapi-frontend/stores/diff";
import { getErrorConfig } from "@/zapi-frontend/shared/config/errorMessages";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { removeContentFromResponse } from "../lib/removeContentFromResponse";

function parseErrors(content: string): string[] | null {
  try {
    const parsed = JSON.parse(content);
    if (parsed.originalError?.message) return [parsed.originalError.message];
    if (parsed.error) return parsed.error.split(";");

    return null;
  } catch {
    return null;
  }
}

export function useChatPolling(projectId: string) {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const requestInFlightRef = useRef(false);
  const processedTasksRef = useRef<Set<number>>(new Set());
  const { isLabs } = useMeStore();
  const queryClient = useQueryClient();

  const {
    addMessage,
    setLoadingState,
    resetLoadingState,
    updatePendingMessage,
    getMessagesWithPending,
    getLoadingState
  } = useChatStore();

  const { createPendingChanges } = useDiffStore();

  const { invalidateChapterCroppedImages, getChapterContent } = useChapterStore();

  const pollGenerationStatus = async () => {
    if (requestInFlightRef.current) {
      return;
    }
    requestInFlightRef.current = true;
    try {
      const _statuses = await queryClient.fetchQuery({
        queryKey: ["chat-status-all", projectId],
        queryFn: () => getStatusAll(projectId)
      });

      const chapterStatuses = _statuses
        .filter((status) => status.type === EGenerationTaskType.chapter && !!status.chapterId)
        .filter((status) => status.id === getLoadingState(projectId, String(status.chapterId)).pendingGenerationId);

      const statuses = [...chapterStatuses];
      const promises = statuses.map(async (statusData) => {
        if (processedTasksRef.current.has(statusData.id)) {
          return;
        }

        const chapterId = statusData.chapterId?.toString() || "";
        const storeKey = chapterId;

        switch (statusData.status) {
          case EGenerationStatus.pending:
          case EGenerationStatus.processing:
            break;

          case EGenerationStatus.streaming:
            if (statusData.result) {
              try {
                const progressData = safeJsonParse(statusData.result);
                if (progressData.type === "chapterGeneration") {
                  setLoadingState(projectId, storeKey, {
                    isGeneratingChapter: true,
                    progressMessage: getProgressMessage(statusData.createdAt),
                    prereqsChaptersToBeGenerated:
                      statusData.prereqs && statusData.prereqs.length > 0 ? statusData.prereqs : null
                  });
                }
              } catch (error) {
                console.error("Error parsing progress data:", error);
              }
            }
            break;

          case EGenerationStatus.ready:
            await confirmPollingEnd(statusData.id);
            processedTasksRef.current.add(statusData.id);
            if (statusData.result) {
              await handleGenerationCompleteForChapter(storeKey, statusData.result);
            }
            break;

          case EGenerationStatus.error: {
            await confirmPollingEnd(statusData.id);
            processedTasksRef.current.add(statusData.id);
            const errors = statusData.result ? parseErrors(statusData.result) : null;

            let humanReadableError: string | undefined;
            if (errors && errors.length > 0) {
              const errorConfig = getErrorConfig(errors[0], !!isLabs);
              humanReadableError = errorConfig.userMessage;
            }

            handleGenerationErrorForChapter(storeKey, humanReadableError || "Generation failed. Please try again.");

            break;
          }

          case EGenerationStatus.cancelled:
            processedTasksRef.current.add(statusData.id);
            resetLoadingState(projectId, storeKey);
            break;

          case EGenerationStatus.processed:
            console.error("Unexpected processed:", statusData.id);
            break;
          default:
            console.error("Unknown generation status:", statusData.status);
        }
      });
      await Promise.all(promises);
    } catch (error) {
      console.error("Error polling generation status:", error);
      return;
    } finally {
      requestInFlightRef.current = false;
    }
  };

  const handleGenerationCompleteForChapter = async (chapterId: string, result: string) => {
    if (!chapterId) return;

    const loadingState = getLoadingState(projectId, chapterId);
    const botMessageId = loadingState.loadingMessageId || `bot-${Date.now()}`;

    try {
      const multiActionMessages = processMultiActionResponse(result);
      const messages = getMessagesWithPending(projectId, chapterId);

      if (multiActionMessages) {
        updatePendingMessage(projectId, chapterId, result);

        multiActionMessages.forEach(async (actionMsg: ChatMessageType) => {
          await processAssistantResponse({
            messageContent: actionMsg.content,
            targetProjectId: projectId,
            targetChapterId: chapterId,
            invalidateChapterCroppedImages,
            chapterContent: getChapterContent(chapterId) || "",
            createPendingChanges
          });

          const cleanedMessage = {
            ...actionMsg,
            content: removeContentFromResponse(actionMsg.content)
          };

          addMessage(projectId, chapterId, cleanedMessage);
        });
      } else {
        if (result.includes("multiAction")) {
          console.error("Can't parse multiAction", result);
          reportErrorToSentry(new Error("Couldn't process the multiaction message"), "useChatPolling", "error", {
            extra: {
              result,
              projectId
            }
          });
          throw new Error("Couldn't process the message");
        }
        const croppedResult = removeContentFromResponse(result);
        const finalBotMessage: ChatMessageType = {
          role: "assistant",
          content: croppedResult,
          id: botMessageId
        };

        updatePendingMessage(projectId, chapterId, finalBotMessage.content);

        if (finalBotMessage.content.length === 0) {
          const additionalData = {
            chatHistory: JSON.stringify([...messages, finalBotMessage]),
            projectId: projectId,
            chapterId: chapterId
          };
          reportErrorToSentry(
            "No content in the Chat response parsed",
            "Chat Island Error",
            "empty_chat_response",
            additionalData
          );
        }

        console.log("Entering processing response", JSON.stringify({ result }, null, 2));
        await processAssistantResponse({
          messageContent: result,
          targetProjectId: projectId,
          targetChapterId: chapterId,
          invalidateChapterCroppedImages,
          chapterContent: getChapterContent(chapterId) || "",
          createPendingChanges
        });
        console.log("Response processed");
        addMessage(projectId, chapterId, finalBotMessage);
        console.log("Message added");
      }

      resetLoadingState(projectId, chapterId);
    } catch (error) {
      console.error("Error handling generation complete:", error);
      reportErrorToSentry(
        "Error handling generation complete",
        "Chat Island Error",
        "error_handling_generation_complete",
        {
          error: error,
          result: result
        }
      );
      handleGenerationErrorForChapter(chapterId, "Error processing generation result");
    }
  };

  // Error message is already processed in confirmPolling
  const handleGenerationErrorForChapter = (chapterId: string, errorMessage: string) => {
    if (!chapterId) return;

    const loadingState = getLoadingState(projectId, chapterId);
    const botMessageId = loadingState.loadingMessageId || `bot-${Date.now()}`;

    const errorContent = JSON.stringify({
      type: "error",
      content: errorMessage
    });

    const errorBotMessage: ChatMessageType = {
      role: "assistant",
      content: errorContent,
      id: botMessageId
    };
    if (loadingState.pendingGenerationId) {
      addMessage(projectId, chapterId, errorBotMessage);
    }
    resetLoadingState(projectId, chapterId);
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    pollGenerationStatus();
    pollingIntervalRef.current = setInterval(() => {
      pollGenerationStatus();
    }, 4000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  return {
    startPolling,
    stopPolling,
    pollOnce: pollGenerationStatus
  };
}
